/* eslint-disable camelcase, max-classes-per-file */
import { loginService, APIBase } from '@agro1desenvolvimento/apis-js-package';
import { ENV } from '../../utilities/constants';

class LaboratorioAPI extends APIBase {
  constructor() {
    super();

    let baseURL = 'https://homologacao-integrations.aqila.com.br';
    if (ENV === 'production') {
      baseURL = 'https://integrations.aqila.com.br';
    }

    this.initialize({ baseURL });

    this.request.interceptors.request.use((request) => {
      const headers = loginService.requestHeaders();
      delete headers['X-Scope'];
      request.headers = {
        ...headers,
        ...request.headers,
        'X-Scope': 'sementes',
      };
      return request;
    });
  }
}

const laboratorioAPI = new LaboratorioAPI();

// Tipos da API de Imagens
export interface PresignedUrlResponse {
  id: string;
  filename: string;
  url: string;
  expires_in: number;
}

export interface ImagemAPI {
  _id: string;
  erp_id: string;
  name: string;
  file: string;
  url?: string; // URL pré-assinada, quando disponível (legado)
  urls?: {
    thumbnail: string;
    fullsize: string;
  };
  gpa: string;
  source_id: string;
  source_type: string;
  integration_status: number;
  size: number;
  created_at: string;
  updated_at: string;
}

export interface ListImagensResponse {
  total: number;
  data: ImagemAPI[];
  page: number;
  per_page: number;
  total_pages: number;
}

class ImagensAPIService {
  private readonly SOURCE_TYPE = 'analise_laboratorio';

  private readonly S3_BASE_URL = 'https://agro1-gestaocloud.s3.sa-east-1.amazonaws.com';

  /**
   * Constrói a URL completa do S3 a partir da key (caminho do arquivo)
   */
  buildS3Url(key: string): string {
    return `${this.S3_BASE_URL}/${key}`;
  }

  /**
   * Obtém uma URL pré-assinada para upload de imagem no S3
   */
  async getPresignedUrl(
    sourceId: string,
    filename: string,
    contentType: string,
  ): Promise<PresignedUrlResponse> {
    const response = await laboratorioAPI.get<PresignedUrlResponse>(
      '/api/v1/agro1/images/presigned_url',
      {
        params: {
          source_type: this.SOURCE_TYPE,
          source_id: sourceId,
          filename,
          content_type: contentType,
        },
      },
    );

    return response.data;
  }

  /**
   * Faz upload do arquivo diretamente para o S3 usando URL pré-assinada
   */
  async uploadToS3(presignedUrl: string, file: Blob, contentType: string): Promise<void> {
    // Upload direto para S3 usando fetch (não passa pelos interceptors do axios)
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
    });

    if (!response.ok) {
      throw new Error(`Erro ao fazer upload para S3: ${response.statusText}`);
    }
  }

  /**
   * Lista imagens de uma análise de laboratório
   */
  async listarImagens(sourceId: string): Promise<ImagemAPI[]> {
    const response = await laboratorioAPI.get<ListImagensResponse>(
      '/api/v1/agro1/images',
      {
        params: {
          source_type: this.SOURCE_TYPE,
          source_id: sourceId,
          deleted: false,
          page: 1,
        },
      },
    );

    return response.data.data;
  }

  /**
   * Remove uma imagem (marca como deletada e remove do S3)
   */
  async removerImagem(erpId: string): Promise<void> {
    await laboratorioAPI.delete(`/api/v1/agro1/images/${erpId}`);
  }
}

export default new ImagensAPIService();
